import { useCallback, useMemo } from 'react';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { byPolygon } from './MasksBadge';
import { cropForTrace } from './partCrop';
import {
  listingImageSrc, listingTracePrompt, runListingMasks, useSubmodelListingCrops,
} from './listingCropsRun';
import type { PartItem } from './partsRun';
import { RUN_MODEL } from './models';
import { useLanguage } from '../../contexts/LanguageContext';

// THE MASKS: tracing each listing box in its own close crop, the question, and what every box
// answered. Beside the phase button with the other calls — it was the LISTING CROPS row's
// "Draw masks" and its badge, in the table's label column.
//
// ONE CALL PER BOX, and the words differ only by the part's name, so the prompt panel carries
// the FIRST box's question and the two pictures it was sent — one instance of the call, as the
// Crops badge shows one photograph. The answers are stacked under it, a section per box. The
// per-part reading, with every box's own crops, is still on a zoomed photograph (MasksBadge).
export function MasksInfo({ docId, items, only, disabled, onStop }: {
  docId: string;
  // The parts this describes — see PartsPhase, which keeps the last list this document had.
  items: PartItem[];
  // The open part, or undefined for every part.
  only?: number;
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const { byPart, busy, toTrace, traced } = useSubmodelListingCrops(docId);
  const masking = toTrace > 0;
  const name = useCallback(
    (i: number) => items[i]?.nameEn || items[i]?.name || t('notice.parts.piece'),
    [items, t],
  );

  // Every box in scope, in part order — the order the press traces them.
  const boxes = useMemo(() => Object.entries(byPart)
    .map(([key, crops]) => ({ part: Number(key), crops: crops ?? [] }))
    .filter(({ part }) => only == null || part === only)
    .sort((a, b) => a.part - b.part)
    .flatMap(({ part, crops }) => crops.map((crop, n) => ({ part, crop, n, of: crops.length }))),
  [byPart, only]);

  const first = boxes[0];
  const load = useCallback(async () => {
    if (!first) return { prompt: '', model: RUN_MODEL };
    const hinted = !!first.crop.rawBbox;
    const [prompt, cut] = await Promise.all([
      listingTracePrompt(name(first.part), hinted),
      cropForTrace(listingImageSrc(first.crop.url), first.crop.rawBbox ?? first.crop.bbox),
    ]);
    return {
      prompt,
      model: RUN_MODEL,
      urls: cut ? [
        { url: cut.image, caption: name(first.part) },
        ...(hinted && cut.hint ? [{ url: cut.hint, caption: t('notice.parts.masksHint') }] : []),
      ] : [],
    };
  }, [first, name, t]);

  const answer = useMemo(() => {
    const said = boxes.filter((b) => b.crop.note);
    if (masking || !said.length) return undefined;
    return {
      label: t('notice.cat.thinking'),
      content: (
        <div className="space-y-4">
          {said.map(({ part, crop, n, of }) => (
            <div key={`${part}-${n}`}>
              <div className="mb-1 font-semibold">
                {name(part)}
                {of > 1 && ` · ${t('notice.parts.masksNth')
                  .replace('{n}', String(n + 1)).replace('{of}', String(of))}`}
              </div>
              {reasoningTooltip(crop.note!.reasoning, byPolygon(crop.note!.raw), t)}
            </div>
          ))}
        </div>
      ),
    };
  }, [boxes, masking, name, t]);

  return (
    <CallBadge
      title={t('notice.parts.masksTitle')}
      named
      run={{
        label: masking
          ? `${t('notice.tip.menuRun')} · ${traced}/${toTrace}`
          : t('notice.tip.menuRun'),
        onRun: () => void runListingMasks(docId, items, RUN_MODEL, only),
        busy: masking,
        // NOTHING TO TRACE WITHOUT BOXES: this pass traces the rectangles the Crops call found.
        disabled: disabled || busy || !boxes.length,
        onStop,
      }}
      promptLabel={t('notice.parts.masksPrompt')}
      load={load}
      revision={first ? `${first.part}-${first.crop.url}` : 'none'}
      answer={answer}
    />
  );
}
